import { MfsFluidVolatileSession } from '../fluid';
import {
  MfsDeleteSignalData,
  MfsFindClientsResponseData,
  MfsSessionDataRequest,
  MfsSessionDataResponse,
  MfsSetSignalData,
  MfsSignalPayload,
  mfsSignalType,
  MfsSignalType,
} from '../fluid/signals';
import { MfsDataStore } from '../store/mfsDataStore';
import { nameof } from '../utility';
import { MfsVolatileSession } from './mfsVolatileSession';
import { MfsInternalSessionData, MfsDefinedValue, MfsSessionData } from './mfsVolatileTypes';

/** Property of MfsDataStore to getMfsItem */
const GET_MFS_ITEM_PROPERTY = nameof<MfsDataStore>('getMfsItem');
/** Property of MfsVolatileSession to getData */
const GET_DATA_PROPERTY = nameof<MfsVolatileSession>('getData');
/** Property of MfsFluidVolatileSession to initialize the sesion */
const INIT_SESSION_PROPERTY = nameof<MfsFluidVolatileSession>('initSession');

// MfsSignalPayload properties
const SENDER_APP_ID = nameof<MfsSignalPayload>('senderAppId');
const PAYLOAD_DATA = nameof<MfsSignalPayload>('data');
const REF_SEQ_NUMBER = nameof<MfsSignalPayload>('refSeqNumber');

// MfsSessionData properties
const DATASET_ID = nameof<MfsSessionData>('dataSetId');
const SESSION_DATA_ID = nameof<MfsSessionData>('sessionDataId');
const SESSION_DATA_VALUE = nameof<MfsSessionData>('value');

/** Seq num property of MfsInternalSessionData */
const MFS_INTERNAL_SEQ_NUM = nameof<MfsInternalSessionData>('seqNum');

/** DatasetId of MfsDeleteSignalData */
const DELETE_SIGNAL_DATASET_ID = nameof<MfsDeleteSignalData>('dataSetId');

/** Client ID of responding client */
const RESPONDER_CLIENT_ID = nameof<MfsFindClientsResponseData>('responderClientId');

/** Client ID of target/buddy client */
const TARGET_CLIENT_ID = nameof<MfsSessionDataRequest>('targetClientId');

/** Array of mfs internal session data objects */
const INTERNAL_SESSION_DATA_ARRAY = nameof<MfsSessionDataResponse>('internalSessionDataArray');

/** Cast session data to internal session data so we can store sequence number */
export const getMfsInternalSessionData = (sessionData: MfsSessionData, seqNum: number): MfsInternalSessionData => {
  return {
    ...sessionData,
    seqNum: seqNum,
  };
};

/** Type check if input is MfsSignalPayload */
export const isMfsSignalPayload = (input: Partial<MfsSignalPayload>): input is MfsSignalPayload =>
  !!input && typeof input === 'object' && SENDER_APP_ID in input && PAYLOAD_DATA in input && REF_SEQ_NUMBER in input;

/** Type check if input is MfsSessionData */
export const isMfsSessionData = (input: MfsDefinedValue | Partial<MfsInternalSessionData>): input is MfsSessionData =>
  !!input &&
  typeof input === 'object' &&
  DATASET_ID in input &&
  SESSION_DATA_ID in input &&
  SESSION_DATA_VALUE in input;

/** Type check if input is MfsInternalSessionData */
export const isMfsInternalSessionData = (
  input: MfsDefinedValue | Partial<MfsInternalSessionData>
): input is MfsInternalSessionData => isMfsSessionData(input) && MFS_INTERNAL_SEQ_NUM in input;

/** Type check if input is MfsSetSignalData */
export const isMfsSetSignalData = (input: MfsDefinedValue | Partial<MfsSetSignalData>): input is MfsSetSignalData =>
  isMfsInternalSessionData(input);

/** Type check if input is MfsDeleteSignalData */
export const isMfsDeleteSignalData = (
  input: MfsDefinedValue | Partial<MfsDeleteSignalData>
): input is MfsDeleteSignalData => !!input && typeof input === 'object' && DELETE_SIGNAL_DATASET_ID in input;

/** Type check if input is MfsFindClientResponse */
export const isMfsFindClientResponse = (
  input: MfsDefinedValue | Partial<MfsFindClientsResponseData>
): input is MfsFindClientsResponseData => !!input && typeof input === 'object' && RESPONDER_CLIENT_ID in input;

/** Type check if input is MfsGetSessionDataRequest */
export const isMfsGetSessionDataRequest = (
  input: MfsDefinedValue | Partial<MfsSessionDataRequest>
): input is MfsSessionDataRequest => !!input && typeof input === 'object' && TARGET_CLIENT_ID in input;

/** Type check if input is MfsGetSessionDataResponse */
export const isMfsGetSessionDataResponse = (
  input: MfsDefinedValue | Partial<MfsSessionDataResponse>
): input is MfsSessionDataResponse => !!input && typeof input === 'object' && INTERNAL_SESSION_DATA_ARRAY in input;

/** Validates that dataObject is of type MfsDataStore */
export function isMfsDataStore(dataObject?: MfsDataStore | MfsVolatileSession): dataObject is MfsDataStore {
  return !!dataObject && GET_MFS_ITEM_PROPERTY in dataObject;
}

/** Validates that signal type is of type MfsSignalType */
export function isMfsSignalType(signalType: string): signalType is MfsSignalType {
  return signalType in mfsSignalType;
}

/** Validates that dataObject is of type MfsVolatileSession */
export function isMfsVolatileSession(dataObject?: MfsDataStore | MfsVolatileSession): dataObject is MfsVolatileSession {
  return !!dataObject && GET_DATA_PROPERTY in dataObject;
}

/** Validates that dataObject is of type MfsFluidVolatileSession */
export function isMfsFluidVolatileSession(
  dataObject: MfsDataStore | MfsVolatileSession
): dataObject is MfsFluidVolatileSession {
  return isMfsVolatileSession(dataObject) && INIT_SESSION_PROPERTY in dataObject;
}
